import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { websitesListQuery } from '@/api/queries';
import type { Dashboard, Website } from '@/api/types';
import { paths, type SiteTab } from '@/app/paths';
import { Drawer, IconTile, Kpi, Pill, type IconName, type Tone } from '@/design-system';
import { alertMeta, alertPath, alertTitle } from '@/features/reports/alerts';
import { alertsQuery } from '@/features/reports/api';
import s from './dashboard.module.css';

type Tv = (k: string, o?: Record<string, unknown>) => string;

/** One thing for the PM to do: an alert of their organizations, or a website's next step. */
type Task = { key: string; icon: IconName; title: string; sub: string | null; tone: Tone; pill: string; to: string | null; urgent: boolean };

/** A website's next step (websites/states.py NEXT_STEP) → its icon and the tab where it is done. */
const STEP: Record<string, { icon: IconName; tab: SiteTab }> = {
  research: { icon: 'scan-search', tab: 'research' },
  structure: { icon: 'layers', tab: 'structure' },
  variants: { icon: 'sparkles', tab: 'variants' },
  approve: { icon: 'badge-check', tab: 'designer' },
  production: { icon: 'shopping-bag', tab: 'sync' },
  domain: { icon: 'globe', tab: 'domains' },
};

/**
 * The PM's tasks (Factory §17): the alerts first (the errors before the rest) — the full feed (`GET /admin/alerts`:
 * domains, integrations and AI budgets too), else the dashboard's — then each website's next step.
 */
export function useMyTasks(dashboardAlerts: Dashboard['alerts']): Task[] {
  const { t } = useTranslation('dashboard');
  const tv = t as unknown as Tv;
  const sites = useQuery(websitesListQuery).data ?? [];
  const feed = useQuery(alertsQuery).data;
  const alerts = feed ?? dashboardAlerts;
  const fromAlerts: Task[] = alerts.map((a, i) => {
    const target = (a.target ?? {}) as Record<string, unknown>;
    const meta = alertMeta(a.kind, target, a.severity);
    return {
      key: `alert-${a.kind}-${i}`,
      icon: meta.icon,
      title: alertTitle(a.kind, a.title, target, tv),
      sub: a.subtitle ?? null,
      tone: meta.tone,
      pill: tv(`alerts.pill.${a.kind}`, { defaultValue: a.kind }),
      to: alertPath(a.kind, target),
      urgent: meta.tone === 'err',
    };
  });
  const fromSteps: Task[] = sites
    .filter((w: Website) => w.next_step && STEP[w.next_step] && w.status !== 'archived')
    .map((w) => {
      const step = STEP[w.next_step as string] as { icon: IconName; tab: SiteTab };
      return {
        key: `step-${w.id}`,
        icon: step.icon,
        title: tv(`tasks.step.${w.next_step}`, { defaultValue: w.next_step ?? '' }),
        sub: `${w.name} · ${w.organization.brand_name}`,
        tone: 'mute' as Tone,
        pill: t('tasks.pill'),
        to: paths.site(w.id, step.tab),
        urgent: false,
      };
    });
  return [...fromAlerts.filter((x) => x.urgent), ...fromAlerts.filter((x) => !x.urgent), ...fromSteps];
}

/**
 * «Мои задачи» (P6-E02, additive; Factory §17): a KPI card in the design's language for a PM — how many
 * things wait on them, the urgent count and the first one; the Notch or a click opens the full list.
 */
export function MyTasksCard({ alerts }: { alerts: Dashboard['alerts'] }) {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const tasks = useMyTasks(alerts);
  const [open, setOpen] = useState(false);
  const urgent = tasks.filter((x) => x.urgent).length;
  const first = tasks[0];
  return (
    <>
      <Kpi
        eyebrow={t('tasks.eyebrow')}
        title={t('tasks.title')}
        value={tasks.length}
        delta={urgent ? t('tasks.urgent', { count: urgent }) : undefined}
        deltaTone="err"
        sub={<span className={s.clip}>{first ? first.title : t('tasks.none')}</span>}
        notch={[{ icon: 'list-checks', label: t('tasks.open'), onClick: () => setOpen(true) }]}
        onClick={() => setOpen(true)}
      />
      <Drawer open={open} onClose={() => setOpen(false)} title={t('tasks.title')} sub={t('tasks.sub')} closeLabel={tc('actions.close_esc')}>
        {tasks.length === 0 ? (
          <div className={s.listEmpty}>{t('tasks.none')}</div>
        ) : (
          <ul className={s.taskList}>
            {tasks.map((x) => (
              <li key={x.key}>
                <button
                  type="button"
                  className={s.row}
                  onClick={() => {
                    setOpen(false);
                    if (x.to) navigate(x.to);
                  }}
                >
                  <IconTile icon={x.icon} size={38} iconSize={17} />
                  <div className={s.alertBody}>
                    <span className={s.alertTitle}>{x.title}</span>
                    {x.sub && <span className={s.alertSub}>{x.sub}</span>}
                  </div>
                  <Pill tone={x.tone}>{x.pill}</Pill>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </>
  );
}
